package org.teddapp.project.ui.settings

import org.teddapp.project.ui.nav.Section
import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.core.tween
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.slideInHorizontally
import androidx.compose.animation.slideOutHorizontally
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.runtime.Composable
import androidx.compose.runtime.MutableState
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color

/**
 * Hôte de drawer Settings — overlay scrim + panneau slide-in à droite.
 *
 * Pattern : on encapsule le contenu d'un écran ; ce composant gère le scrim
 * et la transition. Avant ça, chaque écran dupliquait 25 lignes d'AnimatedVisibility.
 *
 * Usage :
 *   val state = rememberSettingsDrawer()
 *   SettingsDrawerHost(state) {
 *       Column { ... TeddNavRail(onSettingsClick = state::open) ... }
 *   }
 */
/** Ouvre le tiroir des réglages de l'écran courant ; `null` là où il n'y en a pas. */
val LocalOpenSettings = androidx.compose.runtime.staticCompositionLocalOf<(() -> Unit)?> { null }

@Composable
fun SettingsDrawerHost(
    state: SettingsDrawerState,
    onNavigate: ((Section) -> Unit)? = null,
    content: @Composable () -> Unit,
) {
    val bindings = LocalSyncBindings.current
    Box(modifier = Modifier.fillMaxSize()) {
        androidx.compose.runtime.CompositionLocalProvider(LocalOpenSettings provides { state.open() }) {
            content()
        }
        AnimatedVisibility(
            visible = state.isOpen,
            enter = fadeIn(animationSpec = tween(180)),
            exit = fadeOut(animationSpec = tween(180)),
        ) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .background(Color.Black.copy(alpha = 0.32f))
                    .clickable(
                        interactionSource = remember { MutableInteractionSource() },
                        indication = null,
                        onClick = { state.close() },
                    )
            )
        }
        AnimatedVisibility(
            visible = state.isOpen,
            enter = slideInHorizontally(animationSpec = tween(280)) { it },
            exit = slideOutHorizontally(animationSpec = tween(220)) { it },
            modifier = Modifier.align(Alignment.CenterEnd),
        ) {
            val syncState by (bindings?.state?.collectAsState() ?: remember { mutableStateOf(null) })
            SettingsScreen(
                onClose = { state.close() },
                onOpenBackup = onNavigate?.let { nav -> { state.close(); nav(Section.Backup) } },
                onOpenPortfolio = onNavigate?.let { nav -> { state.close(); nav(Section.Portfolio) } },
                syncState = syncState,
                onToggleSync = bindings?.let { b -> { on -> b.onToggle(on) } },
                onSyncNow = bindings?.let { b -> { b.onSyncNow() } },
            )
        }
    }
}

class SettingsDrawerState internal constructor(private val backing: MutableState<Boolean>) {
    val isOpen: Boolean get() = backing.value
    fun open() { backing.value = true }
    fun close() { backing.value = false }
}

@Composable
fun rememberSettingsDrawer(): SettingsDrawerState {
    val st = remember { mutableStateOf(false) }
    return remember(st) { SettingsDrawerState(st) }
}
